import isEqual from 'lodash-es/isEqual'
import pick from 'lodash-es/pick'
import uniq from 'lodash-es/uniq'
import * as Yup from 'yup'

import { getFileDocumentSignedLink } from '../../../api'
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../constants/file'
import { LOCAL_STORAGE_RECIPIENT_EMAIL_FIELD_AUTO_ADD_ADDITIONAL_CONTACTS } from '../../../constants/storage'
import i18n from '../../../i18n'
import { getItemsIds, isValueStringType } from '../../../utils/common'
import { getRecipientRealId, isInfoEmailAccount } from '../../../utils/email'
import {
  getCleanValues,
  objectNumIdValidation,
} from '../../../utils/formHelpers'
import { normalizeHtml, toDocument } from '../../../utils/html'
import { getStorageItem, setStorageItem } from '../../../utils/storage'
import { getUserId } from '../../../utils/user'
import {
  ATTACH_ACTION_SELECT_ACTION_FILE,
  ATTACH_ACTION_SELECT_FILE_FROM_STORAGE,
} from '../../AttachActionSelect'
import {
  LINKED_FILES_BLOCK_FILE_ICON_BASE64,
  LINKED_FILES_BLOCK_FILE_ICON_PUBLIC_URL,
  LINKED_FILES_BLOCK_SELECTOR,
  LINKED_FILES_ITEM_ID_ATTRIBUTE,
} from '../../WysiwygEditor'
import {
  checkEmailForReply,
  getEmailRecipientsCorrectValue,
} from '../Email.utils'

import {
  EMAIL_CREATION_FORM_ACTION_ATTACH_REQUEST_FILE,
  EMAIL_CREATION_FORM_ACTION_ATTACH_TEMPLATE,
  EMAIL_CREATION_FORM_API_FIELDS,
  EMAIL_CREATION_FORM_API_KEY_REPLY_TO_NYLAS,
  EMAIL_CREATION_FORM_API_KEY_SEPARATE_MESSAGES,
  EMAIL_CREATION_FORM_API_KEY_SNIPPET,
  EMAIL_CREATION_FORM_API_KEY_SNIPPET_DEPENDENCIES,
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING,
  EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_PRINTER,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO,
  EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE,
  EMAIL_CREATION_FORM_FIELD_NAME_SIGNATURE,
  EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_THREAD,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_CREATION_FORM_INITIAL_VALUES_TO,
  EMAIL_CREATION_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
  EMAIL_CREATION_FORM_NOTIFICATION_NO_ACTIVE_ACCOUNTS,
  EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE,
  EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT,
  EMAIL_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION,
  EMAIL_CREATION_FORM_SERVER_ERRORS,
  EMAIL_CREATION_FORM_TO_FIELD_MIN_COUNT,
  EMAIL_FORM_TEMPLATE_API_PARAM_BUILDING_ID,
  EMAIL_FORM_TEMPLATE_API_PARAM_FLAT_ID,
  EMAIL_FORM_TEMPLATE_API_PARAM_RECIPIENT_ID,
  EMAIL_FORM_TEMPLATE_API_PARAM_REPLACE_UNDEFINED,
  EMAIL_FORM_TEMPLATE_API_PARAM_REQUEST_ID,
  EMAIL_FORM_TEMPLATE_API_PARAM_UNTAGGED,
  EMAIL_FORM_TEMPLATE_DEPENDENCY_API_PARAM_AUTHOR_ID,
} from './EmailCreationForm.constants'
import {
  normalizeBlocksForEmail,
  normalizeTablesForEmail,
} from './EmailCreationForm.utils.html'

function applyFileLinksToHtml(html, linksById) {
  if (html && linksById && Object.keys(linksById).length > 0) {
    const doc = toDocument(html)
    const anchors = doc.querySelectorAll(`a[${LINKED_FILES_ITEM_ID_ATTRIBUTE}]`)

    anchors.forEach(a => {
      const id = Number(a.getAttribute(LINKED_FILES_ITEM_ID_ATTRIBUTE))
      const url = linksById[id]

      if (Number.isFinite(id) && url) {
        a.setAttribute('href', url)
      }
    })

    return doc.body.innerHTML
  }

  return html
}

function replaceLinkedFilesBlockIconsToPublicUrl(html) {
  if (html) {
    const doc = toDocument(html)
    const blocks = doc.querySelectorAll(`[${LINKED_FILES_BLOCK_SELECTOR}]`)

    if (blocks.length > 0) {
      blocks.forEach(block => {
        const icons = block.querySelectorAll('img')

        icons.forEach(img => {
          if (img.getAttribute('src') === LINKED_FILES_BLOCK_FILE_ICON_BASE64) {
            img.setAttribute('src', LINKED_FILES_BLOCK_FILE_ICON_PUBLIC_URL)
          }
        })
      })

      return doc.body.innerHTML
    }
  }

  return html
}

function getRequestFilesSignedLinkParamsById(values) {
  const storageFiles =
    values?.[EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE] || EMPTY_LIST

  const requestFiles = storageFiles.filter(file => file?.requestId)

  const signedLinkParams = {
    object_id: requestFiles[0]?.requestId,
    scope: FILE_SCOPE_REQUEST,
  }

  return Object.fromEntries(
    getItemsIds(requestFiles).map(id => [Number(id), signedLinkParams])
  )
}

function resetMessageAndSignature(email) {
  return email
    ? {
        ...email,
        [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: EMPTY_STRING,
        [EMAIL_CREATION_FORM_FIELD_NAME_SIGNATURE]: EMPTY_STRING,
      }
    : email
}

function checkEmailAccount(email) {
  if (checkEmailForReply(email)) {
    return email
  }

  return null
}

export function isPrinterRoutedRecipient(item, printer) {
  return !checkEmailForReply(item?.email) && !!printer?.email
}

export function isEmailCreationFormNotificationAiDraftNotApplied(id) {
  return id === EMAIL_CREATION_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED
}

export function isEmailCreationFormNotificationNoActiveAccounts(id) {
  return id === EMAIL_CREATION_FORM_NOTIFICATION_NO_ACTIVE_ACCOUNTS
}

export function getEmailFormValuesWithFilesSignedLinks({
  linkFileIds,
  values,
}) {
  const requestFilesParamsById = getRequestFilesSignedLinkParamsById(values)

  return Promise.all(
    linkFileIds.map(fileId => {
      return getFileDocumentSignedLink({
        id: fileId,
        params: requestFilesParamsById[fileId],
      }).then(response => {
        const url = response?.data?.url

        if (!url) {
          throw {
            response: {
              data: {
                errors:
                  EMAIL_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION,
              },
            },
          }
        }

        return [fileId, url]
      })
    })
  ).then(signedLinksList => {
    const linksById = Object.fromEntries(signedLinksList)

    return {
      ...values,
      [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: applyFileLinksToHtml(
        values?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE],
        linksById
      ),
    }
  })
}

export async function getPreparedEmailFormValues(values) {
  const linkFileIds =
    values?.[EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS] || EMPTY_LIST

  if (linkFileIds.length > 0) {
    return getEmailFormValuesWithFilesSignedLinks({
      linkFileIds,
      values,
    })
  }

  return values
}

export function parseEmailFormRecipientsField(list, printer) {
  return list.map(i => {
    const id = [i?.realId, i?.value, i?.id].find(Number.isSafeInteger)

    if (id !== undefined) {
      return [id, i?.email || printer?.email]
    }

    return i?.value
  })
}

export function getEmailFormRecipientsIds(list) {
  return uniq(list.map(i => i?.realId).filter(Number.isSafeInteger))
}

export function getEmailFormAttachActions({ t, withRequestFolderAction }) {
  return [
    {
      label: t('Emails.AttachFileActionOptionKey'),
      value: ATTACH_ACTION_SELECT_ACTION_FILE,
    },
    {
      label: t('Emails.AttachFileFromStorageActionOptionKey'),
      value: ATTACH_ACTION_SELECT_FILE_FROM_STORAGE,
    },
    ...(withRequestFolderAction
      ? [
          {
            label: t('RequestProfile.AttachFilesFromRequestKey'),
            value: EMAIL_CREATION_FORM_ACTION_ATTACH_REQUEST_FILE,
          },
        ]
      : EMPTY_LIST),
    {
      label: t('Emails.AttachGoogleDriveTemplateActionOptionKey'),
      value: EMAIL_CREATION_FORM_ACTION_ATTACH_TEMPLATE,
    },
  ]
}

export function getEmailFormValidationSchema() {
  return Yup.object({
    [EMAIL_CREATION_FORM_FIELD_NAME_BCC]: Yup.array()
      .of(Yup.object())
      .max(EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT, true),
    [EMAIL_CREATION_FORM_FIELD_NAME_CC]: Yup.array()
      .of(Yup.object())
      .max(EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT, true),
    [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: Yup.mixed()
      .required(true)
      .test({
        message: i18n.t('Error.IncorrectValueKey'),
        test: value => {
          if (isValueStringType(value)) {
            return false
          }

          return objectNumIdValidation(value, 'value')
        },
      }),
    [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: Yup.string()
      .trim()
      .required(true),
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO]: Yup.mixed().when(
      EMAIL_CREATION_FORM_FIELD_NAME_FROM,
      {
        is: value => isInfoEmailAccount(value),
        then: schema =>
          schema.required(true).test({
            message: i18n.t('Error.IncorrectValueKey'),
            test: value => {
              if (isValueStringType(value)) {
                return false
              }

              return !value || objectNumIdValidation(value, 'value')
            },
          }),
      }
    ),
    [EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT]: Yup.string()
      .trim()
      .required(true),
    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: Yup.array()
      .of(Yup.object())
      .min(EMAIL_CREATION_FORM_TO_FIELD_MIN_COUNT, true)
      .when(EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING, {
        is: false,
        then: schema =>
          schema.max(EMAIL_CREATION_FORM_RECIPIENTS_FIELD_MAX_COUNT, true),
      })
      .test({
        message: true,
        test: arr => Array.isArray(arr) && arr.some(i => !!i?.value),
      }),
  })
}

export function getEmailFormPayload(values) {
  const payload = getCleanValues(pick(values, EMAIL_CREATION_FORM_API_FIELDS))

  const {
    [EMAIL_CREATION_FORM_FIELD_NAME_BCC]: bcc,
    [EMAIL_CREATION_FORM_FIELD_NAME_CC]: cc,
    [EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES]: docsTemplates,
    [EMAIL_CREATION_FORM_FIELD_NAME_FILES]: files,
    [EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS]: filesAsLinks,
    [EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE]: filesFromStorage,
    [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: from,
    [EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING]: massMailing,
    [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: message,
    [EMAIL_CREATION_FORM_FIELD_NAME_PRINTER]: printer,
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO]: replyTo,
    [EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE]: selectedTemplate,
    [EMAIL_CREATION_FORM_FIELD_NAME_THREAD]: thread,
    [EMAIL_CREATION_FORM_FIELD_NAME_TO]: to,
  } = values

  payload[EMAIL_CREATION_FORM_FIELD_NAME_FROM] = from?.value
  payload[EMAIL_CREATION_FORM_FIELD_NAME_TO] = parseEmailFormRecipientsField(
    to,
    printer
  )
  payload[EMAIL_CREATION_FORM_FIELD_NAME_CC] = parseEmailFormRecipientsField(
    cc,
    printer
  )

  payload[EMAIL_CREATION_FORM_FIELD_NAME_BCC] = parseEmailFormRecipientsField(
    bcc,
    printer
  )

  if (replyTo) {
    payload[EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO] = replyTo.value
    payload[EMAIL_CREATION_FORM_API_KEY_REPLY_TO_NYLAS] = true
  }

  if (files.length > 0 || filesFromStorage.length > 0) {
    const filesAsLinksSet = new Set(filesAsLinks)

    const attachments = files.filter(file => {
      return !filesAsLinksSet.has(Number(file?.id))
    })

    const storageAttachments = filesFromStorage.filter(file => {
      return !filesAsLinksSet.has(Number(file?.id))
    })

    if (attachments.length > 0 || storageAttachments.length > 0) {
      payload[EMAIL_CREATION_FORM_FIELD_NAME_FILES] = getItemsIds([
        ...attachments,
        ...storageAttachments,
      ])
    }
  }

  if (printer) {
    payload[EMAIL_CREATION_FORM_FIELD_NAME_PRINTER] =
      printer.id || printer.value
  }

  if (message) {
    const normalizedHtml = normalizeHtml(message)
    const htmlWithPublicIcon =
      replaceLinkedFilesBlockIconsToPublicUrl(normalizedHtml)

    payload[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE] = normalizeBlocksForEmail(
      normalizeTablesForEmail(htmlWithPublicIcon)
    )
  }

  if (thread) {
    payload[EMAIL_CREATION_FORM_FIELD_NAME_THREAD] = thread
  }

  if (docsTemplates.length > 0) {
    payload[EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES] =
      docsTemplates.map(
        ({ dependencies, external_variables, id, name, template }) => ({
          dependencies,
          external_variables,
          name,
          template: id || template,
        })
      )
  }

  if (massMailing) {
    payload[EMAIL_CREATION_FORM_API_KEY_SEPARATE_MESSAGES] = true

    if (selectedTemplate?.id) {
      payload[EMAIL_CREATION_FORM_API_KEY_SNIPPET] = selectedTemplate.id
      payload[EMAIL_CREATION_FORM_API_KEY_SNIPPET_DEPENDENCIES] = {
        ...values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE]
          .dependencies,
      }
    }
  }

  return payload
}

export function filterEmailFormBlackListAccounts(value, blackList, printer) {
  if (value.length === 0) {
    return EMPTY_LIST
  }

  let filteredItems = [...value.filter(i => !blackList.includes(i.email))]

  if (printer && blackList.includes(printer?.email)) {
    filteredItems = [...filteredItems.filter(i => checkEmailForReply(i.email))]
  }

  return filteredItems
}

export function getEmailFormFlattenRecipients(values) {
  return [
    ...values[EMAIL_CREATION_FORM_FIELD_NAME_TO],
    ...values[EMAIL_CREATION_FORM_FIELD_NAME_CC],
    ...values[EMAIL_CREATION_FORM_FIELD_NAME_BCC],
  ]
}

export function getEmailFormDocTemplates(docTemplates) {
  return Array.isArray(docTemplates)
    ? docTemplates
    : docTemplates
      ? [docTemplates]
      : EMPTY_LIST
}

export function getEmailFormNextDocTemplates({
  docTemplates,
  editedTemplate,
  template,
}) {
  return docTemplates.includes(editedTemplate)
    ? docTemplates.map(docTemplate =>
        docTemplate === editedTemplate ? template : docTemplate
      )
    : [...docTemplates, template]
}

export function getEmailFormRemainingDocTemplates({
  docTemplates,
  removedTemplate,
}) {
  return docTemplates.filter(docTemplate => docTemplate !== removedTemplate)
}

export function getEmailFormTemplateFileName(template) {
  return `${template.name}.pdf`
}

export function getEmailFormTemplateFile(template) {
  return {
    mime_type: 'application/pdf',
    name: getEmailFormTemplateFileName(template),
    origin: template.pdf_export_link,
    template,
  }
}

export function getEmailFormPreviewValues({ files, signature, values }) {
  return {
    files,
    from: values[EMAIL_CREATION_FORM_FIELD_NAME_FROM],
    signature,
    subject: values[EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT],
    text: values[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE],
    to: values[EMAIL_CREATION_FORM_FIELD_NAME_TO].filter(
      i => i.value !== EMPTY_STRING
    ),
  }
}

export function getEmailFormAccountsListForBlackListVerification(
  values,
  printer
) {
  return [
    ...getEmailRecipientsCorrectValue(getEmailFormFlattenRecipients(values)),
    printer,
  ]
    .map(i => checkEmailAccount(i?.email))
    .filter(i => !!i)
}

export function isEmailFormNoEmptyCcBccRecipients(values) {
  return (
    values[EMAIL_CREATION_FORM_FIELD_NAME_CC].length > 0 ||
    values[EMAIL_CREATION_FORM_FIELD_NAME_BCC].length > 0
  )
}

export function checkEmailFormIsPrivateAccount(values) {
  return !values[EMAIL_CREATION_FORM_FIELD_NAME_FROM].corporate
}

export function getEmailFormTemplateEntityApiParams({
  buildingId,
  buildingIdApiKey = EMAIL_FORM_TEMPLATE_API_PARAM_BUILDING_ID,
  flatId,
  flatIdApiKey = EMAIL_FORM_TEMPLATE_API_PARAM_FLAT_ID,
  requestId,
  requestIdApiKey = EMAIL_FORM_TEMPLATE_API_PARAM_REQUEST_ID,
}) {
  const isValidBuildingId = Number.isFinite(buildingId)
  const isValidFlatId = Number.isFinite(flatId)
  const isValidRequestId = Number.isFinite(requestId)

  return isValidBuildingId || isValidFlatId || isValidRequestId
    ? {
        ...(isValidBuildingId ? { [buildingIdApiKey]: buildingId } : null),
        ...(isValidFlatId
          ? {
              [flatIdApiKey]: flatId,
            }
          : null),
        ...(isValidRequestId
          ? {
              [requestIdApiKey]: requestId,
            }
          : null),
      }
    : null
}

export function getEmailFormTemplateApiParams({
  mass,
  recipients,
  templateEntityApiParams,
}) {
  // Request-bound emails get the template rendered with the request context
  // on selection even for mass mailing — the mass-send endpoint does not
  // render variables in the subject, so they must be resolved in the fields.
  const isRequestEmail = Number.isFinite(
    templateEntityApiParams?.[EMAIL_FORM_TEMPLATE_API_PARAM_REQUEST_ID]
  )

  return {
    ...templateEntityApiParams,
    [EMAIL_FORM_TEMPLATE_API_PARAM_RECIPIENT_ID]:
      recipients.length === 1 && recipients[0].value
        ? (getRecipientRealId(recipients[0]) ?? recipients[0].value)
        : undefined,
    [EMAIL_FORM_TEMPLATE_API_PARAM_REPLACE_UNDEFINED]: !mass,
    [EMAIL_FORM_TEMPLATE_API_PARAM_UNTAGGED]: mass && !isRequestEmail,
  }
}

export function getEmailFormTemplateDependeciesApiParams({
  templateEntityApiParams,
  user,
}) {
  return {
    ...templateEntityApiParams,
    [EMAIL_FORM_TEMPLATE_DEPENDENCY_API_PARAM_AUTHOR_ID]: getUserId(user),
  }
}

export function isEmailCreationFormAvailableTestVariables({
  emptyAccountField,
  emptyRecipients,
  hasDocTemplates,
  mass,
  values,
}) {
  const hasAnyTemplate =
    hasDocTemplates ||
    !!values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE]?.id

  return (
    !emptyRecipients &&
    !emptyAccountField &&
    (mass || hasDocTemplates) &&
    hasAnyTemplate
  )
}

export function isEmailCreationFormAvailablePreview({
  emptyAccountField,
  emptyRecipients,
  hasDocTemplates,
  mass,
}) {
  return (mass || hasDocTemplates) && !emptyRecipients && !emptyAccountField
}

export function resetEmailCreationFormDataRecipients(email) {
  return email
    ? {
        ...email,
        ...EMAIL_CREATION_FORM_INITIAL_VALUES_TO,
      }
    : email
}

export function isDirtyEmailMessage(initialMessage, message) {
  return (
    (message != null &&
      initialMessage != null &&
      normalizeHtml(initialMessage) !== normalizeHtml(message)) ||
    (initialMessage == null) !== (message == null)
  )
}

export function isDirtyEmailCreationForm({ initialValues, values }) {
  return (
    !isEqual(
      resetMessageAndSignature(initialValues),
      resetMessageAndSignature(values)
    ) ||
    isDirtyEmailMessage(
      initialValues?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE],
      values?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]
    )
  )
}

export function isDirtyTableEmailCreationForm({
  initialValues,
  values,
  ...restParams
}) {
  return isDirtyEmailCreationForm({
    ...restParams,
    initialValues: resetEmailCreationFormDataRecipients(initialValues),
    values: resetEmailCreationFormDataRecipients(values),
  })
}

export function parseEmailCreationFormServerError(data, t) {
  const errorText = data?.errors ?? EMPTY_STRING

  if (EMAIL_CREATION_FORM_SERVER_ERRORS[errorText]) {
    switch (EMAIL_CREATION_FORM_SERVER_ERRORS[errorText]) {
      case EMAIL_CREATION_FORM_SERVER_ERRORS[
        EMAIL_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION
      ]:
        return {
          errors: {
            [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: t(
              EMAIL_CREATION_FORM_SERVER_ERRORS[
                EMAIL_CREATION_FORM_SERVER_ERROR_UNABLE_TO_PERFORM_ACTION
              ]
            ),
          },
        }

      default:
        return t(EMAIL_CREATION_FORM_SERVER_ERRORS[errorText])
    }
  }

  return errorText
}

export function getEmailFormSendErrors({ error, t }) {
  const errors = error?.response?.data || EMPTY_OBJECT

  return Object.keys(errors).length === 0
    ? t('Error.CommonApiRequestFailKey')
    : parseEmailCreationFormServerError(errors, t)
}

export function getEmailCreationFormAutoAddAdditionalContactsStorage() {
  return getStorageItem(
    LOCAL_STORAGE_RECIPIENT_EMAIL_FIELD_AUTO_ADD_ADDITIONAL_CONTACTS
  )
}

export function setEmailCreationFormAutoAddAdditionalContactsStorage(value) {
  return setStorageItem(
    LOCAL_STORAGE_RECIPIENT_EMAIL_FIELD_AUTO_ADD_ADDITIONAL_CONTACTS,
    value
  )
}

export function isEmailCreationFormAutoAddAdditionalContactsEnabled() {
  return getEmailCreationFormAutoAddAdditionalContactsStorage() === 'true'
}

export function getRecipientEmailType(option) {
  return option?.emailType || option?.type || null
}

export function isAuthEmail(option) {
  return (
    getRecipientEmailType(option) ===
    EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.main
  )
}

export function isAdditionalEmail(option) {
  return (
    getRecipientEmailType(option) ===
    EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.additional
  )
}

export function isPartnerEmail(option) {
  return (
    getRecipientEmailType(option) ===
    EMAIL_CREATION_FORM_RECIPIENT_FIELD_EMAIL_TYPE.partner
  )
}
